import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Printer, FileText, Package as PackageIcon, X, Copy, ExternalLink, Search } from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import {
  adminListOrders,
  adminUpdateOrder,
  adminOpenPdf,
  adminGenerateBoxtalLabel,
  adminBoxtalStatus,
} from "../lib/api";

const STATUSES = [
  { slug: "nouveau", label: "Nouveau", color: "bg-blue-500/20 text-blue-300 border-blue-500/40" },
  { slug: "en_attente_paiement", label: "Attente paiement", color: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40" },
  { slug: "paye", label: "Payé", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
  { slug: "prepare", label: "Préparé", color: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
  { slug: "expedie", label: "Expédié", color: "bg-violet-500/20 text-violet-300 border-violet-500/40" },
  { slug: "livre", label: "Livré", color: "bg-white/10 text-white/60 border-white/20" },
  { slug: "annule", label: "Annulé", color: "bg-red-500/20 text-red-300 border-red-500/40" },
];

const statusStyle = (slug) => STATUSES.find((s) => s.slug === slug) || STATUSES[0];

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ q: "", status: "all" });
  const [openOrder, setOpenOrder] = useState(null);
  const [trackingDraft, setTrackingDraft] = useState("");
  const [notesDraft, setNotesDraft] = useState("");
  const [boxtalStatus, setBoxtalStatus] = useState({ configured: false, env: "test" });
  const [generatingLabel, setGeneratingLabel] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListOrders();
      setOrders(data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); adminBoxtalStatus().then(setBoxtalStatus).catch(() => {}); }, []);

  const filtered = useMemo(() => {
    return orders
      .filter((o) => filter.status === "all" || o.status === filter.status)
      .filter((o) => {
        if (!filter.q) return true;
        const q = filter.q.toLowerCase();
        return (
          o.full_name.toLowerCase().includes(q) ||
          o.email.toLowerCase().includes(q) ||
          o.id.toLowerCase().includes(q) ||
          (o.tracking_number || "").toLowerCase().includes(q)
        );
      });
  }, [orders, filter]);

  const openDetail = (order) => {
    setOpenOrder(order);
    setTrackingDraft(order.tracking_number || "");
    setNotesDraft(order.admin_notes || "");
  };

  const updateStatus = async (order, status) => {
    try {
      const updated = await adminUpdateOrder(order.id, { status });
      setOrders(orders.map((o) => o.id === order.id ? updated : o));
      if (openOrder && openOrder.id === order.id) setOpenOrder(updated);
      toast.success(`Statut mis à jour : ${statusStyle(status).label}`);
    } catch {
      toast.error("Erreur");
    }
  };

  const saveMeta = async () => {
    try {
      const updated = await adminUpdateOrder(openOrder.id, {
        tracking_number: trackingDraft || null,
        admin_notes: notesDraft || null,
      });
      setOrders(orders.map((o) => o.id === openOrder.id ? updated : o));
      setOpenOrder(updated);
      toast.success("Informations enregistrées");
    } catch {
      toast.error("Erreur");
    }
  };

  const printLabel = (order) => {
    adminOpenPdf(`/admin/orders/${order.id}/label`).catch(() => toast.error("Erreur PDF"));
  };
  const printSlip = (order) => {
    adminOpenPdf(`/admin/orders/${order.id}/packing-slip`).catch(() => toast.error("Erreur PDF"));
  };

  const generateBoxtalLabel = async (order) => {
    if (!boxtalStatus.configured) {
      toast.error("Boxtal n'est pas encore configuré. Ajoutez votre clé API.");
      return;
    }
    setGeneratingLabel(true);
    try {
      const res = await adminGenerateBoxtalLabel(order.id);
      toast.success(`Étiquette générée · N° ${res.tracking_number || "—"}`);
      // Reload the order
      const list = await adminListOrders();
      setOrders(list);
      const fresh = list.find((o) => o.id === order.id);
      if (fresh) setOpenOrder(fresh);
      // Auto-open label
      if (res.label_url) {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        window.open(res.label_url.startsWith("http") ? res.label_url : `${base}${res.label_url}`, "_blank");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur Boxtal");
    } finally {
      setGeneratingLabel(false);
    }
  };

  const copyId = (o) => {
    navigator.clipboard.writeText(o.id);
    toast.success("Numéro de commande copié");
  };

  // Stats
  const stats = useMemo(() => ({
    total: orders.length,
    nouveau: orders.filter((o) => o.status === "nouveau").length,
    prepare: orders.filter((o) => o.status === "prepare").length,
    expedie: orders.filter((o) => o.status === "expedie").length,
    revenue: orders.filter((o) => o.status !== "annule").reduce((s, o) => s + (o.total || 0), 0),
  }), [orders]);

  return (
    <AdminLayout title="Commandes" subtitle="Gérez les commandes reçues, mettez à jour leur statut, générez les étiquettes et bons de préparation.">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
        {[
          { label: "Total", value: stats.total, sub: "commandes" },
          { label: "Nouvelles", value: stats.nouveau, sub: "à traiter" },
          { label: "Préparées", value: stats.prepare, sub: "à expédier" },
          { label: "Expédiées", value: stats.expedie, sub: "en cours" },
          { label: "CA", value: `${stats.revenue.toFixed(0)}€`, sub: "total encaissé" },
        ].map((s) => (
          <div key={s.label} className="border border-white/10 p-4">
            <p className="label-caps text-[color:var(--gold)] text-[9px] mb-1">{s.label}</p>
            <p className="font-serif text-3xl text-white">{s.value}</p>
            <p className="text-[10px] text-white/40 mt-1">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3 flex-1 max-w-lg">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="orders-search"
              value={filter.q}
              onChange={(e) => setFilter({ ...filter, q: e.target.value })}
              placeholder="Rechercher (nom, email, n° suivi)..."
              className="input-line pl-9 border border-white/10 px-4 py-2 w-full"
            />
          </div>
          <select
            data-testid="orders-filter"
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="label-caps text-[10px] bg-transparent border border-white/15 text-white px-3 py-2.5"
          >
            <option value="all" className="bg-[#0a0a0a]">Tous les statuts</option>
            {STATUSES.map((s) => <option key={s.slug} value={s.slug} className="bg-[#0a0a0a]">{s.label}</option>)}
          </select>
        </div>
      </div>

      {/* Orders list */}
      {loading ? (
        <p className="text-white/50 label-caps">Chargement...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24">
          <PackageIcon size={40} strokeWidth={1} className="mx-auto text-white/20 mb-6" />
          <p className="text-white/40 italic">Aucune commande pour le moment.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((o) => {
            const st = statusStyle(o.status);
            return (
              <div
                key={o.id}
                data-testid={`order-row-${o.id.slice(0, 8)}`}
                className="border border-white/10 hover:border-white/25 grid grid-cols-12 gap-4 items-center p-5 transition-colors cursor-pointer"
                onClick={() => openDetail(o)}
              >
                <div className="col-span-6 md:col-span-3">
                  <p className="label-caps text-[color:var(--gold)] text-[9px] mb-1">#{o.id.slice(0, 8).toUpperCase()}</p>
                  <p className="font-serif text-white">{o.full_name}</p>
                  <p className="text-white/40 text-xs mt-0.5">{o.email}</p>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <p className="label-caps text-white/40 text-[9px] mb-1">Date</p>
                  <p className="text-white/80 text-sm">{o.created_at.slice(0, 10)}</p>
                  <p className="text-white/40 text-[10px]">{o.created_at.slice(11, 16)}</p>
                </div>
                <div className="col-span-4 md:col-span-2">
                  <p className="label-caps text-white/40 text-[9px] mb-1">Transporteur</p>
                  <p className="text-white/80 text-sm">{o.carrier_name || "—"}</p>
                  <p className="text-white/40 text-[10px]">{o.items.length} art.</p>
                </div>
                <div className="col-span-4 md:col-span-2">
                  <p className="label-caps text-white/40 text-[9px] mb-1">Total</p>
                  <p className="font-serif text-[color:var(--gold)] text-lg">{o.total.toFixed(2)}€</p>
                </div>
                <div className="col-span-4 md:col-span-3 flex md:justify-end">
                  <span className={`label-caps text-[10px] px-3 py-1.5 border ${st.color}`}>
                    {st.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Order detail modal */}
      {openOrder && (
        <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 md:p-8 overflow-y-auto">
          <div data-testid="order-modal" className="bg-[#0A0A0A] border border-white/10 w-full max-w-4xl my-8">
            <div className="flex items-center justify-between p-6 border-b border-white/10 sticky top-0 bg-[#0A0A0A] z-10">
              <div>
                <p className="label-caps text-[color:var(--gold)] text-[10px]">Commande #{openOrder.id.slice(0, 8).toUpperCase()}</p>
                <h2 className="font-serif text-2xl text-white">{openOrder.full_name}</h2>
                <p className="text-white/40 text-xs mt-1">
                  {openOrder.created_at.slice(0, 10)} · {openOrder.created_at.slice(11, 16)}
                  <button onClick={() => copyId(openOrder)} className="ml-3 text-white/50 hover:text-white inline-flex items-center gap-1">
                    <Copy size={10} /> Copier ID
                  </button>
                </p>
              </div>
              <button onClick={() => setOpenOrder(null)} className="text-white/60 hover:text-white" data-testid="order-modal-close">
                <X size={22} />
              </button>
            </div>

            <div className="p-6 md:p-8 space-y-8">
              {/* Status pipeline */}
              <div>
                <p className="label-caps text-[color:var(--gold)] text-[10px] mb-4">Statut</p>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s.slug}
                      data-testid={`status-${s.slug}`}
                      onClick={() => updateStatus(openOrder, s.slug)}
                      className={`label-caps text-[10px] px-3 py-2 border transition-all ${
                        openOrder.status === s.slug ? s.color : "border-white/10 text-white/50 hover:border-white/30"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions PDF */}
              <div className="space-y-3">
                <div className={`border p-5 ${boxtalStatus.configured ? "border-[color:var(--gold)] bg-[color:var(--gold)]/5" : "border-white/10 border-dashed"}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <p className="label-caps text-[color:var(--gold)] text-[10px] mb-1">Étiquette officielle transporteur · Boxtal</p>
                      <p className="text-white text-sm mt-1">
                        {boxtalStatus.configured
                          ? `Génération automatique avec N° de suivi ${boxtalStatus.env === "prod" ? "(PRODUCTION)" : "(TEST)"}`
                          : "En attente de vos clés API Boxtal"}
                      </p>
                      {openOrder.tracking_number && (
                        <p className="text-white/60 text-xs mt-2">Numéro actuel : <span className="text-[color:var(--gold)] font-mono">{openOrder.tracking_number}</span></p>
                      )}
                    </div>
                    <button
                      data-testid="boxtal-label-btn"
                      onClick={() => generateBoxtalLabel(openOrder)}
                      disabled={!boxtalStatus.configured || generatingLabel}
                      className="btn-gold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {generatingLabel ? "Génération..." : "Générer l'étiquette officielle"}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <button
                    data-testid="print-invoice-btn"
                    onClick={() => adminOpenPdf(`/admin/orders/${openOrder.id}/invoice`).catch(() => toast.error("Erreur PDF"))}
                    className="btn-outline"
                  >
                    <FileText size={14} /> Facture {openOrder.invoice_number || "(auto)"}
                  </button>
                  <button
                    data-testid="print-label-btn"
                    onClick={() => printLabel(openOrder)}
                    className="btn-outline"
                  >
                    <Printer size={14} /> Étiquette interne
                  </button>
                  <button
                    data-testid="print-slip-btn"
                    onClick={() => printSlip(openOrder)}
                    className="btn-outline"
                  >
                    <FileText size={14} /> Bon de préparation
                  </button>
                </div>
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <p className="label-caps text-[color:var(--gold)] text-[10px] mb-4">Adresse de livraison</p>
                  <div className="border border-white/10 p-5">
                    <p className="font-serif text-white text-lg">{openOrder.full_name}</p>
                    <p className="text-white/70 text-sm mt-2">{openOrder.address}</p>
                    <p className="text-white/70 text-sm">{openOrder.postal_code} {openOrder.city}</p>
                    <p className="text-white/70 text-sm">{openOrder.country}</p>
                    <div className="mt-4 pt-4 border-t border-white/10 space-y-1">
                      <p className="text-white/60 text-sm">📧 {openOrder.email}</p>
                      <p className="text-white/60 text-sm">📞 {openOrder.phone}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="label-caps text-[color:var(--gold)] text-[10px] mb-4">Livraison & Suivi</p>
                  <div className="border border-white/10 p-5 space-y-4">
                    <div>
                      <p className="label-caps text-white/40 text-[9px] mb-1">Transporteur</p>
                      <p className="font-serif text-white">{openOrder.carrier_name || "Non défini"}</p>
                    </div>
                    <div>
                      <label className="label-caps text-white/40 text-[9px] block mb-2">N° de suivi</label>
                      <input
                        data-testid="tracking-input"
                        value={trackingDraft}
                        onChange={(e) => setTrackingDraft(e.target.value)}
                        placeholder="À saisir après création de l'étiquette carrier"
                        className="input-line"
                      />
                    </div>
                    <div>
                      <label className="label-caps text-white/40 text-[9px] block mb-2">Notes internes</label>
                      <textarea
                        rows={3}
                        value={notesDraft}
                        onChange={(e) => setNotesDraft(e.target.value)}
                        className="input-line resize-none"
                        placeholder="Note privée sur cette commande"
                      />
                    </div>
                    <button
                      data-testid="save-order-meta"
                      onClick={saveMeta}
                      className="label-caps text-[10px] text-[color:var(--gold)] border-b border-[color:var(--gold)] pb-1"
                    >
                      Enregistrer
                    </button>
                  </div>
                </div>
              </div>

              {/* Items */}
              <div>
                <p className="label-caps text-[color:var(--gold)] text-[10px] mb-4">Articles ({openOrder.items.length})</p>
                <div className="border border-white/10 divide-y divide-white/10">
                  {openOrder.items.map((it, i) => (
                    <div key={`${it.product_id || it.name}-${it.volume}-${i}`} className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-serif text-white">{it.name}</p>
                        <p className="label-caps text-white/40 text-[10px] mt-1">{it.volume} · Qté {it.quantity}</p>
                      </div>
                      <p className="font-serif text-[color:var(--gold)]">{(it.price * it.quantity).toFixed(2)}€</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="border-t border-white/10 pt-6 space-y-2 text-sm">
                <div className="flex justify-between text-white/60"><span>Sous-total</span><span>{openOrder.subtotal.toFixed(2)}€</span></div>
                {openOrder.promo_code && (
                  <div className="flex justify-between text-[color:var(--gold)]"><span>Code {openOrder.promo_code}</span><span>—</span></div>
                )}
                <div className="flex justify-between text-white/60">
                  <span>Livraison · {openOrder.carrier_name || "—"}</span>
                  <span>{openOrder.shipping ? `${openOrder.shipping.toFixed(2)}€` : "Offerte"}</span>
                </div>
                <div className="flex justify-between text-white font-serif text-xl pt-3 border-t border-white/10">
                  <span>Total</span><span>{openOrder.total.toFixed(2)}€</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOrders;
